import React, { useRef } from 'react';
import { InputText, Button, Select, TextArea } from '@insurancemenu/imenu-components-lib';
import { useRootStore } from 'store/rootStore';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import SubtitleWithLines from 'components/common/general/SubtitleWithLines';
import CarrierRulesViolation from 'components/common/general/CarrierRulesViolation';
import { createStyles } from './styles';
import BookmarkedSection from './BookmarkedSection';

interface ContactMeFormProps {
  showFullTitle: boolean;
}

const ContactMeForm = observer(({ showFullTitle }: ContactMeFormProps): JSX.Element => {
  const classes = createStyles();
  // Mobx store
  const {
    contactMeModalStore: {
      form: { fields, onFieldChange, basicFieldsValidationsPassed },
      loading,
      cart: { cartItems },
      allPortfolios,
      ineligibleState,
      allSortedProductLineIds,
      submitForm,
      checkCarrierProductLinesRules,
    },
    domainStore: { closeChoiceModal },
    geoStore: { countiesAsSelectOptions, wrongZipCodeKeyedIn },
  } = useRootStore();

  const scrollRef = useRef<HTMLDivElement>(null);

  const convertedCartItems = cartItems.map((cartItem) => toJS(cartItem)).slice();
  const allProductLineIds = allSortedProductLineIds();
  const invalidProductLineIdsWithMessage = checkCarrierProductLinesRules();
  const convertedPortfoliosByPLine = allPortfolios.map((portfolio) => toJS(portfolio)).slice();

  const isCartEmpty = convertedCartItems.length === 0;

  const BookmarkedPlans = allProductLineIds.map((id) => {
    const portfolioByPLine = convertedPortfoliosByPLine.find((portfolio) => portfolio[id]);
    const carrier = portfolioByPLine ? portfolioByPLine[id][0]?.products[0].carrier : { id: '', name: '', logo: '' };
    const cartItemsByPLine = cartItems.filter(({ productLineId }) => productLineId === id);
    return <BookmarkedSection key={id} carrier={carrier} cartItemsByPLine={cartItemsByPLine} productLineId={id} />;
  });

  const workingWithAgentOptions: OptionType[] = [
    {
      value: 'yes',
      label: 'Yes',
    },
    {
      value: 'no',
      label: 'No',
    },
  ];

  const zipCodeValidationMessage = (): string => {
    if (fields.zipCode.unpassedValidationMsg) return fields.zipCode.unpassedValidationMsg;
    if (wrongZipCodeKeyedIn) return 'Invalid Zip Code';
    if (ineligibleState) return 'Ineligible state to browse';
    return '';
  };

  const onClose = (): void => {
    closeChoiceModal();
    scrollRef.current?.scrollTo(0, 0);
  };

  const onAction = (e: React.FormEvent): void => {
    e.preventDefault();
    void submitForm(onClose);
  };

  return (
    <>
      {!loading && (
        <div ref={scrollRef}>
          <div className={classes.body}>
            {showFullTitle ? (
              <h3 className={classes.modalTitle}>Connect with a team member</h3>
            ) : (
              <SubtitleWithLines text="Connect with a team member" />
            )}
            <p className={classes.subtitle}>
              Complete the form below and one of our knowledgeable team members will reach out to assist you within 1-2
              business days.
            </p>
            <form className={classes.form} onSubmit={onAction} id="contact_me_form">
              <div className={classes.inputContainer}>
                <InputText
                  label="Business or Company Name"
                  name="companyName"
                  value={fields.companyName.value}
                  className={classes.fullInputLayout}
                  onChange={onFieldChange}
                  validationPassed={!fields.companyName.unpassedValidationMsg}
                  validationMessage={fields.companyName.unpassedValidationMsg || ''}
                />
              </div>
              <div className={classes.inputContainer}>
                <InputText
                  label="Number of employees"
                  name="numberOfEmployees"
                  value={fields.numberOfEmployees.value}
                  className={classes.halfInputLayout}
                  onChange={onFieldChange}
                  validationPassed={!fields.numberOfEmployees.unpassedValidationMsg}
                  validationMessage={fields.numberOfEmployees.unpassedValidationMsg || ''}
                />
                <Select
                  label="Are you working with a broker or agent"
                  name="workingWithAgent"
                  value={fields.workingWithAgent.value}
                  className={classes.halfInputLayout}
                  options={workingWithAgentOptions}
                  onChange={onFieldChange}
                  validationPassed={!fields.workingWithAgent.unpassedValidationMsg}
                  validationMessage={fields.workingWithAgent.unpassedValidationMsg || ''}
                />
              </div>
              <h3 className={classes.sectionTitle}>Main Contact Information</h3>
              <div className={classes.inputContainer}>
                <InputText
                  label="First Name"
                  name="firstName"
                  value={fields.firstName.value}
                  className={classes.halfInputLayout}
                  onChange={onFieldChange}
                  validationPassed={!fields.firstName.unpassedValidationMsg}
                  validationMessage={fields.firstName.unpassedValidationMsg || ''}
                />
                <InputText
                  label="Last Name"
                  name="lastName"
                  value={fields.lastName.value}
                  className={classes.halfInputLayout}
                  onChange={onFieldChange}
                  validationPassed={!fields.lastName.unpassedValidationMsg}
                  validationMessage={fields.lastName.unpassedValidationMsg || ''}
                />
                <InputText
                  label="Email"
                  name="email"
                  value={fields.email.value}
                  className={classes.halfInputLayout}
                  onChange={onFieldChange}
                  validationPassed={!fields.email.unpassedValidationMsg}
                  validationMessage={fields.email.unpassedValidationMsg || ''}
                />
                <InputText
                  label="Phone Number"
                  name="phone"
                  value={fields.phone.value}
                  className={classes.halfInputLayout}
                  onChange={onFieldChange}
                  validationPassed={!fields.phone.unpassedValidationMsg}
                  validationMessage={fields.phone.unpassedValidationMsg || ''}
                />
              </div>
              {allProductLineIds.length > 0 && (
                <>
                  {isCartEmpty ? (
                    <h3 className={classes.sectionTitle}>Select the Product Lines You are Interested In:</h3>
                  ) : (
                    <>
                      <h3 className={classes.bookmarkedSectionTitle}>Bookmarked plans:</h3>
                      <p className={classes.bookmarkedPlansSubtitle}>
                        We will let the team member know you are interested in these plans
                      </p>
                    </>
                  )}

                  <div className={classes.productLineSections}>{BookmarkedPlans}</div>
                </>
              )}
              <h3 className={classes.sectionTitle}>Physical Address (Optional)</h3>
              <div className={classes.inputContainer}>
                <InputText
                  label="City"
                  name="city"
                  value={fields.city.value}
                  onChange={onFieldChange}
                  validationMessage={fields.city.unpassedValidationMsg || ''}
                  validationPassed={!fields.city.unpassedValidationMsg}
                  className={classes.fullInputLayout}
                />
                <InputText
                  label="Street"
                  name="street"
                  value={fields.street.value}
                  onChange={onFieldChange}
                  validationMessage={fields.street.unpassedValidationMsg || ''}
                  validationPassed={!fields.street.unpassedValidationMsg}
                  className={classes.halfInputLayout}
                />
                <InputText
                  label="Street2"
                  name="street2"
                  value={fields.street2.value}
                  onChange={onFieldChange}
                  validationMessage={fields.street2.unpassedValidationMsg || ''}
                  validationPassed={!fields.street2.unpassedValidationMsg}
                  className={classes.halfInputLayout}
                />
                <InputText
                  label="Zip Code"
                  name="zipCode"
                  onChange={onFieldChange}
                  value={fields.zipCode.value}
                  validationMessage={zipCodeValidationMessage()}
                  validationPassed={!zipCodeValidationMessage()}
                  className={classes.thirdInputLayout}
                  maxLength={5}
                />
                <InputText
                  label="State"
                  name="state"
                  disabled
                  value={fields.state.value}
                  validationMessage={fields.state.unpassedValidationMsg || ''}
                  validationPassed={!fields.state.unpassedValidationMsg}
                  className={classes.thirdInputLayout}
                />
                <Select
                  label="County"
                  name="county"
                  onChange={onFieldChange}
                  value={fields.county.value}
                  options={countiesAsSelectOptions}
                  validationMessage={fields.county.unpassedValidationMsg || ''}
                  validationPassed={!fields.county.unpassedValidationMsg}
                  className={classes.thirdInputLayout}
                />
              </div>
              <div>
                <h3 className={classes.sectionTitle}>Notes</h3>
                <TextArea
                  name="notes"
                  value={fields.notes.value}
                  onChange={onFieldChange}
                  width="100%"
                  height="16rem"
                  className={classes.notesTextArea}
                />
              </div>
            </form>
          </div>
          <CarrierRulesViolation invalidProductLineIdsWithMessage={invalidProductLineIdsWithMessage} />
          <footer className={classes.footer}>
            <div className={classes.actionsBlock}>
              <Button type="outlined" onClick={closeChoiceModal} size="large">
                Cancel
              </Button>
              <Button
                color="primary"
                htmlType="submit"
                size="large"
                htmlForm="contact_me_form"
                disabled={!basicFieldsValidationsPassed || invalidProductLineIdsWithMessage.length > 0}
              >
                Submit
              </Button>
            </div>
          </footer>
        </div>
      )}
    </>
  );
});

export default ContactMeForm;
